import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Table, DatePicker, Button, Select, message, Alert, Spin } from 'antd';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { profitAndLossDetail, getCustomerQb } from '~/services/hooks/quickbooksApi';
import { useTranslation } from 'react-i18next';
import Box from '~/components/Box';
import { parseProfitAndLossDetailData } from '~/Utils/parseReportData';
import './style.css';

const { RangePicker } = DatePicker;

const ReportControls = React.memo(({ dateRange, onDateChange, onGenerate, loading, t }) => (
  <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
    <RangePicker 
      value={dateRange}
      onChange={onDateChange}
      format="YYYY-MM-DD"
      allowClear={false}
    />
    <Button type="primary" onClick={onGenerate} loading={loading}>
      {t('screens:reports.generateReport')}
    </Button>
  </div>
));

const CustomerHeader = React.memo(({ customerName }) => (
  customerName && <h2 style={{ padding: '20px 0' }}>{customerName}</h2>
));

const NoDataAlert = React.memo(({ t }) => (
  <Alert
    message={t('screens:reports.noData')}
    description={t('screens:reports.noDataDescription')}
    type="info"
    showIcon
    style={{ marginBottom: 16 }}
  />
));

export default function ProfitAndLossDetailReportWB({ project, customer }) {
  const { t } = useTranslation();
  const { description: name } = project; 
  const qbId = customer?.qbId;
  
  // Estados
  const [dateRange, setDateRange] = useState(() => [
    moment().startOf('year'),
    moment().endOf('year')
  ]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [reportState, setReportState] = useState({
    dataSource: [],
    loading: false,
    reportLoaded: false,
    noData: false
  });

  // Referência para controle do mounted state
  const isMounted = useRef(true);

  // Função para verificar se há linhas
  const hasRows = useCallback(arr => Array.isArray(arr) && arr.length > 0, []);

  // Reset do relatório
  const resetReport = useCallback(() => {
    if (!isMounted.current) return;
    
    setSelectedCustomer(null);
    setCustomerOptions([]);
    setReportState({
      dataSource: [],
      loading: false,
      reportLoaded: false,
      noData: false
    });
    // Reset das datas para os valores iniciais (criando novos objetos moment)
    setDateRange([moment().startOf('year'), moment().endOf('year')]);
  }, []);

  // Busca dos dados do relatório
  const fetchData = useCallback(async (start, end, customerIdOverride) => {
    const customerId = customerIdOverride ?? selectedCustomer?.id;
    if (!customerId) return;

    setReportState(prev => ({ ...prev, loading: true, reportLoaded: false, noData: false }));

    try {
      const resp = await profitAndLossDetail({
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        customerId,
      });

      if (isMounted.current) {
        if (hasRows(resp?.Rows?.Row)) {
          setReportState(prev => ({
            ...prev,
            dataSource: parseProfitAndLossDetailData(resp),
            reportLoaded: true
          }));
        } else {
          setReportState(prev => ({
            ...prev,
            dataSource: [],
            noData: true
          }));
        }
      }
    } catch (err) {
      if (isMounted.current) {
        console.error(err);
        message.error(t('screens:reports.generateError'));
      }
    } finally {
      if (isMounted.current) {
        setReportState(prev => ({ ...prev, loading: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id, t, hasRows]);

  // Busca do cliente e relatório
  const fetchCustomerAndReport = useCallback(async (customerName, dates = dateRange) => {
    if (!customerName) return resetReport();

    try {
      const resp = await getCustomerQb({ name: customerName });
      const customers = resp?.QueryResponse?.Customer ?? [];
      const options = customers.map(c => ({
        label: c.FullyQualifiedName,
        value: c.Id,
      }));
      
      if (isMounted.current) {
        setCustomerOptions(options);

        if (options.length > 0) {
          const first = options[0];
          setSelectedCustomer({ id: first.value, name: first.label });
          await fetchData(dates[0], dates[1], first.value);
        } else {
          resetReport();
          message.info(t('screens:reports.noCustomerFound'));
        }
      }
    } catch (err) {
      if (isMounted.current) {
        console.error(err);
        message.error(t('screens:reports.customerSearchError'));
      }
    }
  }, [dateRange, fetchData, resetReport, t]);

  // Efeito de inicialização - executa apenas uma vez
  useEffect(() => {
    isMounted.current = true;
    
    const initialize = async () => {
      if (qbId) {
        setSelectedCustomer({ id: qbId, name });
        await fetchData(dateRange[0], dateRange[1], qbId);
      } else {
        await fetchCustomerAndReport(name);
      }
    };

    initialize();

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const handleCustomerSearch = useCallback(async value => {
    if (!value) return setCustomerOptions([]);
    try {
      const resp = await getCustomerQb({ name: value });
      const customers = resp?.QueryResponse?.Customer ?? [];
      setCustomerOptions(
        customers.map(c => ({
          label: c.FullyQualifiedName,
          value: c.Id,
        }))
      );
    } catch (err) {
      console.error(err);
      message.error(t('screens:reports.customerSearchError'));
    }
  }, [t]);

  const debouncedSearch = useMemo(() => debounce(handleCustomerSearch, 500, {
    leading: false,
    trailing: true,
    maxWait: 1000
  }), [handleCustomerSearch]);

  // Manipulador de mudança de datas
  const handleDateChange = useCallback((dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    }
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: d => (d ? moment(d).format('YYYY-MM-DD') : ''),
    },
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      render: (text, record) => <span style={{ paddingLeft: record.level * 20 }}>{text}</span>,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: text =>
        text === ''
          ? ''
          : new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(Number(text)),
    },
  ], []);

  return (
    <Box>
      <Spin
        spinning={reportState.loading}
        tip={t('screens:reports.loading')}
        size="large"
        style={{ width: '100%' }}
      >
        <ReportControls
          dateRange={dateRange}
          onDateChange={handleDateChange}
          onGenerate={() => fetchData(dateRange[0], dateRange[1])}
          loading={reportState.loading}
          t={t}
        />

        {/* Hidden Select - mantido para compatibilidade */}
        <Select
          showSearch
          placeholder={t('screens:reports.customerSearchPlaceholder')}
          style={{ width: 600, display: 'none' }}
          filterOption={false}
          onSearch={debouncedSearch}
          onChange={(val, opt) => {
            setSelectedCustomer({ id: val, name: opt.label });
            fetchData(dateRange[0], dateRange[1], val);
          }}
          notFoundContent={null}
          options={customerOptions}
          value={selectedCustomer?.id}
        />

        <CustomerHeader customerName={selectedCustomer?.name} />

        {reportState.noData && <NoDataAlert t={t} />}

        <Table
          columns={columns}
          dataSource={reportState.dataSource}
          pagination={false}
          bordered
          rowClassName={record => (record.isSummary ? 'summary-row' : '')}
          expandable={{
            defaultExpandedRowKeys: reportState.dataSource
              .filter(item => item.level === 0 && item.children?.length)
              .map(item => item.key),
            expandRowByClick: true,
          }}
          aria-label="Profit and Loss Detail Report"
        />
      </Spin>
    </Box>
  );
}
